import React, { ReactElement } from 'react';
import { Row, Column } from 'mage-components';
import styled from 'styled-components';

import { FieldOption } from '@mjolnir/mf-fields-ts/src/types/Field.type';

import { CardComponent } from '@/components';
import { ErrorAlert } from '@/components/ErrorAlert';
import { ErrorMessage } from '@/components/ErrorMessage';

type Props<T> = {
  value?: T;
  onChange: (value: T) => void;
  options?: FieldOption[];
  error?: string;
  showErrorOnTop?: boolean;
};

export const CardsSelector = <T,>({
  value,
  onChange,
  options = [],
  error,
  showErrorOnTop,
}: Props<T>): ReactElement<Props<T>> => {
  const isSelected = (currentValue: T): boolean => currentValue === value;

  return (
    <>
      {showErrorOnTop && error ? (
        <StyledWrapper>
          <ErrorAlert error={error} />
        </StyledWrapper>
      ) : null}
      <Row>
        {Object.values(options).map(option => (
          <Column columns={{ desktop: 6, mobile: 12 }} key={option.key}>
            <CardComponent
              error={!showErrorOnTop && error}
              id={option.key}
              isSelectedFn={isSelected}
              onChange={onChange}
              enumTranslationFunction={enumTranslationFunction(option)}
            />
          </Column>
        ))}
      </Row>
      {!showErrorOnTop && error ? <ErrorMessage message={error} /> : null}
    </>
  );
};

const StyledWrapper = styled.div`
  position: fixed;
  bottom: auto;
  z-index: 99;
  left: 50%;
  top: 0;
  transform: translate(-50%, 0);
  border-radius: 1em;
`;

const enumTranslationFunction = (option: FieldOption) => (
  t: (string) => string,
  key: string,
): Record<string, string | undefined> => {
  /**
   * TODO: What to do with these defaults (?)
   */
  const DEFAULT_LABEL = `products-fe.options.business_concepts.${key}.label`;
  const DEFAULT_DESCRIPTION = `products-fe.options.business_concepts.${key}.description`;

  return {
    title: t(option.label || DEFAULT_LABEL) || option.fallbackLabel,
    description: t(option.description || DEFAULT_DESCRIPTION) || option.fallbackDescription,
  };
};
